/**
 * Copyright ©2023 Drivepoint
 */

import React, {useEffect, useState} from "react";
import {Add, Business, Cancel, CheckBox, Delete, Group, Person, Public, Security} from "@mui/icons-material";
import {Button, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, Select} from "@mui/material";
import {Form, Formik} from "formik";
import type {Props} from "mui-modal-provider/dist/types";
import {useStore} from "@bainbridge-growth/node-frontend";
import ActiveUsersAndCompaniesStore from "@services/store/ActiveUsersAndCompaniesStore";
import PermissionGroupsStore from "@services/store/PermissionGroupsStore";
import DPButton from "../../../../DPButton/DPButton";
import "./ACLDialog.css";

export type ACLDialogProps = {
  item: any;
  onChange: (rules: any[]) => void;
  open: boolean;
} & Props;

export default function ACLDialog(props: ACLDialogProps): any {

  const types: any[] = [
    {id: "public", label: "Public", icon: <Public />},
    {id: "role", label: "Role", icon: <Security />},
    {id: "uid", label: "User", icon: <Person />},
    {id: "company", label: "Company", icon: <Business />},
    {id: "group", label: "Group", icon: <Group />}
  ];

  const methods: any[] = [
    {id: "POST", label: "Create"},
    {id: "GET", label: "Read"},
    {id: "PATCH", label: "Update"},
    {id: "DELETE", label: "Delete"},
    {id: "ALL", label: "All"}
  ];

  const roles: any[] = [
    {id: "user", label: "User"},
    {id: "admin", label: "Admin"},
    {id: "superAdmin", label: "Super Admin"}
  ];

  const [permissionGroups, permissionGroupsStore] = useStore<any[], PermissionGroupsStore>(PermissionGroupsStore);
  const [activeUsersAndCompanies] = useStore<any, ActiveUsersAndCompaniesStore>(ActiveUsersAndCompaniesStore);

  const [rules, setRules] = useState<string[]>([]);
  const [users, setUsers] = useState<any[]>();
  const [companies, setCompanies] = useState<any[]>();
  const [invalid, setInvalid] = useState<boolean>();
  const [type, setType] = useState<string>();
  const [target, setTarget] = useState<string>();
  const [targets, setTargets] = useState<any[]>();
  const [method, setMethod] = useState<string>();

  useEffect(() => {
    if (!activeUsersAndCompanies?.users || !activeUsersAndCompanies?.companies) { return; }
    setUsers(activeUsersAndCompanies.users);
    setCompanies(activeUsersAndCompanies.companies);
  }, [activeUsersAndCompanies]);

  useEffect(() => {
    setRules(props.item?.acl ?? []);
  }, [props.item]);

  useEffect(() => {
    setInvalid(rules.some(it => !it));
  }, [rules]);

  useEffect(() => {
    setTarget(undefined);
    let targets: any[] | undefined;
    switch (type) {
      case "uid":
        targets = users?.map(it => ({id: it.uid, label: it.email}));
        break;
      case "role":
        targets = roles;
        break;
      case "company":
        targets = companies?.map(it => ({id: it.id, label: it.name}));
        break;
      case "group":
        targets = permissionGroups.map(it => ({id: it.id, label: it.name}));
        break;
    }
    setTargets(targets?.sort((a: any, b: any) => a.label.localeCompare(b.label)));
  }, [type]);

  function close(): void {
    props.onClose();
  }

  async function onSubmit(): Promise<void> {
    setTimeout(() => props.onChange(rules));
    close();
  }

  function addRule(): void {
    setType(undefined);
    setTarget(undefined);
    setMethod(undefined);
    setRules([...rules, ""]);
  }

  function createRule(index: number): void {
    const rule = [type, target, method].filter(it => it).join(":");
    setRules(rules.toSpliced(index, 1, rule));
  }

  function cancelRule(index: number): void {
    setRules(rules.toSpliced(index, 1));
  }

  function deleteRule(index: number): void {
    setRules(rules.toSpliced(index, 1));
  }

  function renderLoadingTarget(target: any, fallback: any, loaded: boolean): any {
    if (target) { return <span>{target}</span>; }
    const classes: string[] = [];
    if (!loaded) { classes.push("loading"); }
    return <span className={`${classes.join(" ")}`}>{fallback}</span>;
  }

  function renderPublicRule(args: string[], index: number): any {
    const method = methods.find(it => it.id === args[0]);
    return <div key={`rule_${index}`} className="acl-dialog-rule">
      <div className="acl-dialog-rule-icon"><Public /></div>
      <div className="acl-dialog-rule-target"><span style={{fontStyle: "italic", color: "var(--GREY-500)"}}>public</span></div>
      <div className="acl-dialog-rule-method">{method?.label ?? "???"}</div>
      <div className="acl-dialog-rule-delete-button acl-dialog-rule-icon-button"><Button onClick={() => deleteRule(index)}><Delete /></Button></div>
    </div>;
  }

  function renderRoleRule(args: string[], index: number): any {
    const role = args[0];
    const method = methods.find(it => it.id === args[1]);
    return <div key={`rule_${index}`} className="acl-dialog-rule">
      <div className="acl-dialog-rule-icon"><Security /></div>
      <div className="acl-dialog-rule-target">{role}</div>
      <div className="acl-dialog-rule-method">{method?.label ?? "???"}</div>
      <div className="acl-dialog-rule-delete-button acl-dialog-rule-icon-button"><Button onClick={() => deleteRule(index)}><Delete /></Button></div>
    </div>;
  }

  function renderUidRule(args: string[], index: number): any {
    const uid = args[0];
    const method = methods.find(it => it.id === args[1]);
    const user = users?.find(user => user.uid === uid);
    return <div key={`rule_${index}`} className="acl-dialog-rule">
      <div className="acl-dialog-rule-icon"><Person /></div>
      <div className="acl-dialog-rule-target">{renderLoadingTarget(user?.email, uid, !!users)}</div>
      <div className="acl-dialog-rule-method">{method?.label ?? "???"}</div>
      <div className="acl-dialog-rule-delete-button acl-dialog-rule-icon-button"><Button onClick={() => deleteRule(index)}><Delete /></Button></div>
    </div>;
  }

  function renderCompanyRule(args: string[], index: number): any {
    const id = args[0];
    const method = methods.find(it => it.id === args[1]);
    const company = companies?.find(company => company.id === id);
    return <div key={`rule_${index}`} className="acl-dialog-rule">
      <div className="acl-dialog-rule-icon"><Business /></div>
      <div className="acl-dialog-rule-target">{renderLoadingTarget(company?.name, id, !!companies)}</div>
      <div className="acl-dialog-rule-method">{method?.label ?? "???"}</div>
      <div className="acl-dialog-rule-delete-button acl-dialog-rule-icon-button"><Button onClick={() => deleteRule(index)}><Delete /></Button></div>
    </div>;
  }

  function renderGroupRule(args: string[], index: number): any {
    const id = args[0];
    const method = methods.find(it => it.id === args[1]);
    const group = permissionGroups.find(it => it.id === id);
    return <div key={`rule_${index}`} className="acl-dialog-rule">
      <div className="acl-dialog-rule-icon"><Group /></div>
      <div className="acl-dialog-rule-target">{renderLoadingTarget(group?.name, id, !permissionGroupsStore.loading)}</div>
      <div className="acl-dialog-rule-method">{method?.label ?? "Inherit"}</div>
      <div className="acl-dialog-rule-delete-button acl-dialog-rule-icon-button"><Button onClick={() => deleteRule(index)}><Delete /></Button></div>
    </div>;
  }

  function onTypeChange(event: any): void {
    setTarget(undefined);
    setTargets(undefined);
    setType(event.target.value);
  }

  function renderTypeSelector(): any {
    return <Select size="small" value={type ?? ""} MenuProps={{disablePortal: true}} onChange={onTypeChange} renderValue={value => {
      const type = types.find(type => type.id === value);
      return <div className="acl-dialog-type-value">{type?.icon ?? type.id}</div>;
    }}>
      {types.map(type => <MenuItem className="acl-dialog-type-menu-item" key={type.id} value={type.id}>{type.icon} {type.label}</MenuItem>)}
    </Select>;
  }

  function onTargetChange(event: any): void {
    setTarget(event.target.value);
  }

  function renderTargetSelector(): any {
    if (targets == null) { return; }
    return <Select displayEmpty renderValue={value => {
      if (!value) {
        const thing = types.find(it => it.id === type);
        return <div className="acl-dialog-rule-target-placeholder">Select {thing?.label}...</div>;
      }
      const target = targets.find(target => target.id === value);
      return target?.label ?? value;
    }} size="small" fullWidth value={target ?? ""} MenuProps={{disablePortal: true}} onChange={onTargetChange}>
      {targets?.map(target => <MenuItem key={target.id} value={target.id}>{target.label}</MenuItem>)}
    </Select>;
  }

  function renderMethodSelector(): any {
    return <Select size="small" value={method ?? ""} MenuProps={{disablePortal: true}} onChange={event => setMethod(event.target.value)}>
      {methods.map(method => <MenuItem key={method.id} value={method.id}>{method.label}</MenuItem>)}
    </Select>;
  }

  function isNewRuleValid(): boolean {
    switch (type) {
      case "group": return !!target;
      case "public": return !!method;
      default: return !!target && !!method;
    }
  }

  function renderAddRule(index: number): any {
    return <div key={`rule_${index}`}>
      <div className="acl-dialog-rule-creator-note">Select a rule type from the dropdown, then a target (if needed), and a permission (if needed):</div>
      <div key={`rule_${index}`} className="acl-dialog-rule acl-dialog-rule-creator">
        <div className="acl-dialog-rule-icon">{renderTypeSelector()}</div>
        <div className="acl-dialog-rule-target">{renderTargetSelector()}</div>
        <div className="acl-dialog-rule-method">{renderMethodSelector()}</div>
        <div className="acl-dialog-rule-add-buttons">
          <div className="acl-dialog-rule-create-button acl-dialog-rule-icon-button">
            <Button disabled={!isNewRuleValid()} onClick={() => createRule(index)}><CheckBox /></Button>
          </div>
          <div className="acl-dialog-rule-cancel-button acl-dialog-rule-icon-button">
            <Button className="acl-dialog-rule-cancel-button acl-dialog-rule-icon-button" onClick={() => cancelRule(index)}><Cancel /></Button>
          </div>
        </div>
      </div>
    </div>;
  }

  function renderRule(rule: string, index: number): any {
    const [type, ...args] = rule.split(":");
    switch (type) {
      case "public": return renderPublicRule(args, index);
      case "role": return renderRoleRule(args, index);
      case "uid": return renderUidRule(args, index);
      case "company": return renderCompanyRule(args, index);
      case "group": return renderGroupRule(args, index);
      case "": return renderAddRule(index);
      default: throw new Error(`unknown rule type: ${type}`);
    }
  }

  function renderRules(): any {
    return <div className="acl-dialog-rules">
      {rules.map(renderRule)}
    </div>;
  }

  return <Dialog className="acl-dialog" open={props.open} onClose={() => {}} maxWidth="sm" fullWidth={true}>
    <DialogTitle className="acl-dialog-title">{`Access Control For "${props.item?.name}"`}</DialogTitle>
    <Formik enableReinitialize initialValues={{}} onSubmit={onSubmit}>
      {() => {
        return <Form>
          <DialogContent className="acl-dialog-content">
            <div className="acl-dialog-content-container">
              {renderRules()}
            </div>
            <div className="acl-dialog-add-button">
              <Button disabled={invalid} startIcon={<Add />} onClick={addRule}>ADD RULE</Button>
            </div>
          </DialogContent>
          <DialogActions className="acl-dialog-actions">
            <DPButton variant="text" color="secondary" label="CANCEL" onClick={() => close()} />
            <div className="acl-dialog-button-spacer" />
            <DPButton disabled={invalid} label="SAVE" submit />
          </DialogActions>
        </Form>;
      }}
    </Formik>
  </Dialog>;

}
